import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { buildPrintPreviewHtml, sanitizePdfFileNamePart } from '@/utils/printPreview'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'
import { useLazyGetBbgnPrintPreviewQuery } from '@/store/api/saleControlApi'

// Access port: rpCtrlSalesBBGN ("Biên bản giao nhận" / goods handover record).
// frmCtrlSalesMainSubFormSaleAlc btnPrintBBGN_Click → set query
// CtrlSalesQryBBGNPrint (public_v_sale_allocation_print_scs WHERE delivery_order
// + sale_contract) → DoCmd.OpenReport "rpCtrlSalesBBGN". Self-fetching dialog:
// lazy-loads the HTML preview from the BE endpoint, mirrors DoPrintDialog
// (iframe srcDoc preview + Print → printAndUploadReport). canEdit gates Print.

interface SalesAllocationBbgnPrintDialogProps {
  open: boolean
  /**
   * Selected allocation row `delivery_order`. Access guard: empty/null → warn
   * "Delivery order must be not null!" and never opens the report.
   */
  deliveryOrder: string
  /** Selected allocation row `sale_contract` (second WHERE key). */
  saleContract: string
  /** Print button enabled only with write access (4-layer permission rule). */
  canEdit?: boolean
  onClose: () => void
}

export default function SalesAllocationBbgnPrintDialog({
  open,
  deliveryOrder,
  saleContract,
  canEdit = true,
  onClose,
}: SalesAllocationBbgnPrintDialogProps) {
  const [toastMessage, setToastMessage] = useState('')
  const [fetchPreview, { data, isFetching, isError, error }] = useLazyGetBbgnPrintPreviewQuery()

  const iframeTitle = 'BBGN Print Preview'
  const hasDeliveryOrder = Boolean(deliveryOrder?.trim())

  // Access guard + lazy fetch: only request the report once the dialog is opened
  // for a row that actually has a delivery_order.
  useEffect(() => {
    if (!open) return
    if (!hasDeliveryOrder) {
      setToastMessage('Delivery order must be not null!')
      onClose()
      return
    }
    void fetchPreview({ deliveryOrder: deliveryOrder.trim(), saleContract: saleContract?.trim() ?? '' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasDeliveryOrder, deliveryOrder, saleContract])

  // Surface a fetch error (English only — never reveal Access/report names).
  useEffect(() => {
    if (open && isError && error) {
      setToastMessage('Unable to load the handover record preview.')
    }
  }, [open, isError, error])

  const html = data?.html ?? ''
  const previewHtml = html ? buildPrintPreviewHtml(html, { styleId: 'bbgn-print-preview-overrides' }) : ''
  // BE already returns the fully-formed `data.title` = "Print Preview - BBGN
  // (<delivery order>)". Use it directly for the tooltip — do NOT re-wrap it
  // (would produce "Print Preview - BBGN (Print Preview - BBGN (...))").
  // Fallback rebuilds the same shape from the clean prop.
  const title = data?.title || (deliveryOrder ? `Print Preview - BBGN (${deliveryOrder})` : 'Print Preview - BBGN')
  const loading = open && hasDeliveryOrder && isFetching

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.35)',
          }}
        >
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog
        open={open && Boolean(html)}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box
            component="span"
            title={title}
            sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}
          >
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            BBGN
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">
              Cancel
            </Button>
            {canEdit && (
              <Button
                variant="contained"
                size="small"
                disabled={!html}
                onClick={() => {
                  // Tên file = "<contract no>_<delivery order>_BBGN.pdf". Dùng
                  // deliveryOrder (prop sạch), KHÔNG dùng idPart (= data.title =
                  // nguyên chuỗi "Print Preview - BBGN (...)").
                  const bbgnName = [(saleContract ?? '').trim(), (deliveryOrder ?? '').trim()].filter(Boolean).join('_')
                  const printTitle = `${sanitizePdfFileNamePart(bbgnName || 'handover_record')}_BBGN.pdf`
                  void printAndUploadReport({
                    iframeTitle,
                    reportName: 'rpCtrlSalesBBGN',
                    printTitle,
                    html: previewHtml,
                    onUploadError: setToastMessage,
                  })
                }}
              >
                Print
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box
            component="iframe"
            title={iframeTitle}
            srcDoc={previewHtml}
            sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }}
          />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
